import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { focusRing } from 'ui/a11y';
import { LisUiRoot } from 'ui/lis-ui-root';

import { AccountMenu } from './account-menu';
import { up } from './breakpoints';
import { CloseIcon, MenuIcon } from './icons';
import { LanguageSwitch } from './language-switch';
import { Lockup } from './lockup';
import { MainNav } from './main-nav';
import { MobileDrawer } from './mobile-drawer';
import { useHeaderModel } from './use-header-model';

const Bar = styled.header`
  position: relative; /* the containing block for the account menu and Administreerimine panels */
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 4px;
  height: 56px;
  padding: 0 6px 0 16px;
  background: ${p => p.theme.colors.surface};
  border-bottom: 2px solid ${p => p.theme.colors.borderSubtle};

  ${up('sm')} {
    height: 64px;
    gap: 8px;
    padding: 0 16px 0 10px;
  }

  ${up('md')} {
    gap: ${p => p.theme.space[3]};
    padding: 0 ${p => p.theme.space[3]};
  }

  ${up('lg')} {
    height: 72px;
    gap: ${p => p.theme.space[4]};
    padding: 0 ${p => p.theme.space[3]} 0 ${p => p.theme.space[4]};
  }
`;

const LockupWrap = styled.div`
  display: flex;
  align-items: center;
  min-width: 0; /* let Lockup's own ellipsis truncate instead of overflowing the bar */
  order: 1;
`;

const RightCluster = styled.div`
  order: 3;
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const HamburgerButton = styled.button`
  order: 4;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: ${p => p.theme.colors.brown};
  cursor: pointer;

  &[aria-expanded='true'] {
    background: ${p => p.theme.colors.yellowPale};
  }

  ${up('sm')} {
    /* sm order: hamburger first, then the lockup — everything else keeps its base order. */
    order: -1;
  }

  ${up('md')} {
    display: none;
  }

  ${focusRing}
`;

/**
 * The responsive app header. All the actual sign-in/visibility logic lives in `useHeaderModel`;
 * this file is layout only.
 */
export function Header() {
  const { t } = useTranslation();
  const model = useHeaderModel();
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  const primaryNavLabel = t('header.primaryNav');
  const drawerOpen = model.openOverlay === 'drawer';

  return (
    <LisUiRoot>
      <Bar>
        {/*
          First in the DOM (tab order), not last — it's visually first at sm+ anyway (CSS order,
          below), and even at xs, where it's visually last, a hamburger-first tab order is the
          better compromise: it's reachable immediately rather than after every visible nav
          control.
        */}
        {model.hasNavItems && (
          <HamburgerButton
            ref={hamburgerRef}
            type="button"
            aria-label={t(drawerOpen ? 'header.closeMenu' : 'header.openMenu')}
            aria-expanded={drawerOpen}
            onClick={() => model.toggleOverlay('drawer')}
          >
            {drawerOpen ? <CloseIcon /> : <MenuIcon />}
          </HamburgerButton>
        )}

        <LockupWrap>
          <Lockup isLink={model.isAllowed} />
        </LockupWrap>

        {model.hasNavItems && (
          <MainNav
            label={primaryNavLabel}
            showGuestPermits={model.showGuestPermitsNav}
            showStatistics={model.showStatisticsNav}
            adminGroups={model.adminGroups}
            adminActive={model.isAdminOrOrgViewActive}
            openOverlay={model.openOverlay}
            onToggleAdmin={() => model.toggleOverlay('admin')}
            onCloseAdmin={model.closeOverlay}
          />
        )}

        <RightCluster>
          {model.showBarLanguageSwitch && <LanguageSwitch language={model.language} onChange={model.changeLanguage} />}
          {model.accountVariant !== 'none' && (
            <AccountMenu
              variant={model.accountVariant}
              firstName={model.firstName}
              roleLabel={model.roleLabel}
              showRolePicker={model.showRolePicker}
              switchableRoles={model.switchableRoles}
              activeRole={model.activeRole}
              onSwitchRole={model.switchRole}
              showLanguage={model.accountVariant !== 'msal-not-allowed'}
              language={model.language}
              onChangeLanguage={model.changeLanguage}
              onLogout={model.accountVariant === 'guest-checkpoint' ? model.guestCheckpointLogout : model.msalLogout}
              open={model.openOverlay === 'account'}
              onToggle={() => model.toggleOverlay('account')}
              onClose={model.closeOverlay}
            />
          )}
        </RightCluster>
      </Bar>

      {drawerOpen && (
        <MobileDrawer
          label={primaryNavLabel}
          showGuestPermits={model.showGuestPermitsNav}
          showStatistics={model.showStatisticsNav}
          adminGroups={model.adminGroups}
          onClose={model.closeOverlay}
          returnFocusRef={hamburgerRef}
        />
      )}
    </LisUiRoot>
  );
}

export default Header;
